<template>
  <div class="path-surveys-list p-4">
    <RecordListPrintHeader title="進路希望/進路活動記録/指導者間コメント" />

    <div
      v-for="member in members"
      :key="member.id"
      :data-member-id="member.id"
      class="path-surveys-list__user-row border mb-4">
      <div class="d-flex">
        <div class="path-surveys-list__user-info">
          <div
            class="path-surveys-list__member-info-clickable"
            @click="goToProfile(member.id)">
            <RecordListMemberInfo
              :member="member"
              :highlighted="member.id === highlightMemberId" />
          </div>
          <button
            v-if="shouldShowExpandButton(member)"
            type="button"
            class="path-surveys-list__expand-btn d-none d-md-block"
            @click="toggleExpand(member.id)">
            {{ isExpanded(member.id) ? '▲ 折りたたむ' : '▼ すべて表示' }}
          </button>
        </div>

        <div class="path-surveys-list__records-area d-flex">
          <div class="path-surveys-list__section path-surveys-list__section--survey">
            <div class="path-surveys-list__section-title">
              進路希望
            </div>
            <div
              v-if="!member.pathSurvey"
              class="fs-s text--grey05">
              進路希望データなし
            </div>
            <table
              v-else
              class="path-surveys-list__info-table">
              <tbody>
                <tr>
                  <th>希望エリア</th>
                  <td>{{ formatOptions(member.pathSurvey.residencePreference, residencePreferenceOptions) || '-' }}</td>
                </tr>
                <tr>
                  <th>希望進路</th>
                  <td>{{ formatOptions(member.pathSurvey.preferredPath, preferredPathOptions) || '-' }}</td>
                </tr>
                <tr>
                  <th>受験タイプ</th>
                  <td>{{ formatOptions(member.pathSurvey.examType, examTypeOptions) || '-' }}</td>
                </tr>
                <tr>
                  <th>最寄駅</th>
                  <td>{{ member.pathSurvey.nearestStation || '-' }}</td>
                </tr>
                <tr>
                  <th>通学時間</th>
                  <td>{{ member.pathSurvey.accessTime || '-' }}</td>
                </tr>
                <tr>
                  <th>生徒希望欄</th>
                  <td class="path-surveys-list__pre-wrap">
                    {{ member.pathSurvey.preference || '-' }}
                  </td>
                </tr>
                <tr>
                  <th>保護者希望欄</th>
                  <td class="path-surveys-list__pre-wrap">
                    {{ member.pathSurvey.parentPreference || '-' }}
                  </td>
                </tr>
                <tr>
                  <th>第1希望</th>
                  <td>{{ member.pathSurvey.firstPreferredSchool || '-' }}</td>
                </tr>
                <tr>
                  <th>第2希望</th>
                  <td>{{ member.pathSurvey.secondPreferredSchool || '-' }}</td>
                </tr>
                <tr>
                  <th>第3希望</th>
                  <td>{{ member.pathSurvey.thirdPreferredSchool || '-' }}</td>
                </tr>
                <tr>
                  <th>第4希望</th>
                  <td>{{ member.pathSurvey.fourthPreferredSchool || '-' }}</td>
                </tr>
                <tr>
                  <th>第5希望</th>
                  <td>{{ member.pathSurvey.fifthPreferredSchool || '-' }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div
            class="path-surveys-list__section path-surveys-list__section--activities"
            :style="!isExpanded(member.id) ? collapseStyle(member.id) : {}">
            <div class="path-surveys-list__section-title">
              進路活動記録
            </div>
            <div
              v-if="!member.pathActivities.length"
              class="fs-s text--grey05">
              活動記録なし
            </div>
            <div
              v-else
              class="path-surveys-list__activities-wrap">
              <div
                v-for="activity in member.pathActivities"
                :key="activity.id"
                class="path-surveys-list__activity-card">
                <div class="path-surveys-list__activity-date">
                  {{ activity.activityAt | prettyDate }}
                </div>
                <div class="path-surveys-list__activity-desc">
                  {{ activity.description || '-' }}
                </div>
              </div>
            </div>
          </div>

          <div
            class="path-surveys-list__section path-surveys-list__section--member-comments"
            :style="!isExpanded(member.id) ? collapseStyle(member.id) : {}">
            <div class="path-surveys-list__section-title">
              メンバーへのコメント
            </div>
            <div
              v-if="!member.pathActivityComments.length"
              class="fs-s text--grey05">
              コメントなし
            </div>
            <div
              v-else
              class="path-surveys-list__member-comments">
              <div
                v-for="comment in member.pathActivityComments"
                :key="comment.id"
                class="path-surveys-list__member-comment">
                <div class="path-surveys-list__comment-author">
                  {{ comment.createdBy ? comment.createdBy.name : '-' }}
                  <span class="path-surveys-list__comment-date">{{ comment.createdAt | prettyDate }}</span>
                </div>
                <div class="path-surveys-list__comment-detail">
                  {{ comment.detail }}
                </div>
              </div>
            </div>
          </div>

          <div
            class="path-surveys-list__section path-surveys-list__section--staff-comments"
            :style="!isExpanded(member.id) ? collapseStyle(member.id) : {}">
            <div class="path-surveys-list__section-title">
              指導者間コメント
            </div>
            <div
              v-if="!member.managerComments.length"
              class="fs-s text--grey05">
              コメントなし
            </div>
            <div
              v-else
              class="path-surveys-list__manager-comments">
              <div
                v-for="comment in member.managerComments"
                :key="comment.id"
                class="path-surveys-list__manager-comment">
                <div class="path-surveys-list__comment-author">
                  {{ comment.createdBy ? comment.createdBy.name : '-' }}
                  <span class="path-surveys-list__comment-date">{{ comment.createdAt | prettyDate }}</span>
                </div>
                <div class="path-surveys-list__comment-detail">
                  {{ comment.detail }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <button
        v-if="shouldShowExpandButton(member)"
        type="button"
        class="path-surveys-list__expand-btn path-surveys-list__expand-btn--mobile d-md-none"
        @click="toggleExpand(member.id)">
        {{ isExpanded(member.id) ? '▲ 折りたたむ' : '▼ すべて表示' }}
      </button>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import RecordListPrintHeader from '@/components/view/records/RecordListPrintHeader.vue'
import RecordListMemberInfo from '@/components/view/records/RecordListMemberInfo.vue'
import {
  residencePreferenceOptions,
  preferredPathOptions,
  examTypeOptions,
} from '@/helper/profile'
import { buildMemberFilterApiParams, buildOrderingByFilterQuery, resolveFilterQuery } from '@/helper/records'

export default {
  components: {
    RecordListPrintHeader,
    RecordListMemberInfo,
  },
  data() {
    return {
      members: [],
      expandedMemberIds: {},
      surveyHeights: {},
      rightSectionHeights: {},
      highlightMemberId: null,
    }
  },
  computed: {
    ...mapState('auth', ['user']),
    ...mapState('recordProfile', ['filterParams']),
    residencePreferenceOptions() {
      return residencePreferenceOptions
    },
    preferredPathOptions() {
      return preferredPathOptions
    },
    examTypeOptions() {
      return examTypeOptions
    },
  },
  watch: {
    members() {
      this.$nextTick(() => this.measureSurveyHeights())
    },
  },
  async created() {
    this.$store.commit('startLoading')
    try {
      await this.fetchData()
    } finally {
      this.$store.commit('finishLoading')
    }
    this.$nextTick(() => {
      const memberId = this.$store.state.recordProfile.returnMemberId
      if (memberId) {
        this.$store.commit('recordProfile/clearReturnMemberId')
        this.highlightMemberId = memberId
        const el = document.querySelector(`[data-member-id="${memberId}"]`)
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }
    })
  },
  mounted() {
    let timer = null
    this._resizeHandler = () => {
      clearTimeout(timer)
      timer = setTimeout(() => this.measureSurveyHeights(), 150)
    }
    window.addEventListener('resize', this._resizeHandler)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this._resizeHandler)
  },
  methods: {
    buildFilterParams() {
      const q = resolveFilterQuery(this.$route.query, this.filterParams)
      return buildMemberFilterApiParams(q)
    },
    async fetchData() {
      const q = resolveFilterQuery(this.$route.query, this.filterParams)
      const params = buildMemberFilterApiParams(q)
      const [surveyRes, activityRes, activityCommentRes, managerCommentRes, usersRes] = await Promise.all([
        this.$axios.get('/admin/profiles/path-surveys/', { params }),
        this.$axios.get('/admin/profiles/path-activities/', { params }),
        this.$axios.get('/admin/profiles/path-activity-comments/', { params }),
        this.$axios.get('/admin/profiles/manager-comments/', { params: { ...params, ordering: 'created_at' } }),
        this.$axios.get('/users/', { params: { ...params, ordering: buildOrderingByFilterQuery(q), limit: 999999 } }),
      ])
      const users = usersRes.data.results || []
      const surveyMap = (surveyRes.data || []).reduce((acc, item) => {
        acc[item.id] = item.pathSurvey
        return acc
      }, {})
      const activityMap = (activityRes.data || []).reduce((acc, item) => {
        acc[item.id] = item.pathActivities
        return acc
      }, {})
      const activityCommentMap = (activityCommentRes.data || []).reduce((acc, item) => {
        acc[item.id] = item.pathActivityComments || []
        return acc
      }, {})
      const managerCommentMap = (managerCommentRes.data || []).reduce((acc, item) => {
        acc[item.id] = item.managerComments || []
        return acc
      }, {})
      this.members = users.map(user => ({
        ...user,
        pathSurvey: surveyMap[user.id] || null,
        pathActivities: activityMap[user.id] || [],
        pathActivityComments: activityCommentMap[user.id] || [],
        managerComments: managerCommentMap[user.id] || [],
      }))
    },
    formatOptions(values, options) {
      if (!values || !values.length) return ''
      return values
        .map(v => {
          const opt = options.find(o => o.value === v)
          return opt ? opt.text : v
        })
        .join('、')
    },
    goToProfile(memberId) {
      this.$router.push({
        path: `/members/${memberId}/profile`,
        query: { backTo: 'recordList' },
      })
    },
    hasActivities(member) {
      return member.pathActivities.length > 0 || member.pathActivityComments.length > 0 || member.managerComments.length > 0
    },
    shouldShowExpandButton(member) {
      if (!this.hasActivities(member)) return false
      return (this.rightSectionHeights[member.id] || 0) > this.collapseLimit(member.id)
    },
    isExpanded(memberId) {
      return !!this.expandedMemberIds[memberId]
    },
    toggleExpand(memberId) {
      this.$set(this.expandedMemberIds, memberId, !this.expandedMemberIds[memberId])
    },
    collapseLimit(memberId) {
      return Math.max(this.surveyHeights[memberId] || 0, 280)
    },
    collapseStyle(memberId) {
      if (!this.surveyHeights[memberId]) return {}
      return { maxHeight: this.collapseLimit(memberId) + 'px', overflow: 'hidden' }
    },
    measureSurveyHeights() {
      this.members.forEach(member => {
        const rowEl = this.$el.querySelector(`[data-member-id="${member.id}"]`)
        if (!rowEl) return
        const surveySection = rowEl.querySelector('.path-surveys-list__section--survey')
        if (!surveySection) return
        const style = getComputedStyle(surveySection)
        const paddingTop = parseInt(style.paddingTop) || 0
        const paddingBottom = parseInt(style.paddingBottom) || 0
        const titleEl = surveySection.querySelector('.path-surveys-list__section-title')
        const titleStyle = titleEl ? getComputedStyle(titleEl) : null
        const titleHeight = titleEl ? titleEl.offsetHeight + (parseInt(titleStyle.marginBottom) || 0) : 0
        const tableEl = surveySection.querySelector('.path-surveys-list__info-table')
        const noDataEl = surveySection.querySelector('.fs-s.text--grey05')
        const contentHeight = tableEl ? tableEl.offsetHeight : noDataEl ? noDataEl.offsetHeight : 0
        this.$set(this.surveyHeights, member.id, paddingTop + titleHeight + contentHeight + paddingBottom)

        const selectors = [
          '.path-surveys-list__section--activities',
          '.path-surveys-list__section--member-comments',
          '.path-surveys-list__section--staff-comments',
        ]
        const maxRightHeight = selectors.reduce((max, sel) => {
          const el = rowEl.querySelector(sel)
          return el ? Math.max(max, el.scrollHeight) : max
        }, 0)
        this.$set(this.rightSectionHeights, member.id, maxRightHeight)
      })
    },
  },
}
</script>

<style lang="scss">
@page {
  size: A4 landscape;
}
</style>

<style lang="scss" scoped>
.path-surveys-list {
  background-color: white;
  min-height: 100vh;

  &__user-row {
    page-break-inside: avoid;
  }

  &__user-info {
    width: 180px;
    flex-shrink: 0;
    padding: 12px;
    border-right: 1px solid $grey11;
    display: flex;
    flex-direction: column;
  }

  &__member-info-clickable {
    cursor: pointer;

    &:hover {
      background-color: $grey03;
    }
  }

  &__expand-btn {
    margin-top: auto;
    padding-top: 8px;
    font-size: $fs-s;
    color: $l-blue;
    background: none;
    border: none;
    cursor: pointer;
    text-align: right;
    align-self: flex-end;

    &:hover {
      text-decoration: underline;
    }

    &--mobile {
      width: 100%;
      text-align: center;
      border-top: 1px solid $grey11;
      padding: 10px 12px;
    }
  }

  &__records-area {
    flex: 1;
    overflow-x: auto;
  }

  &__section {
    padding: 12px;
    border-right: 1px solid $grey11;

    &:last-child {
      border-right: none;
    }

    &--survey {
      width: 320px;
      flex-shrink: 0;
    }

    &--activities {
      flex: 1;
      min-width: 200px;
    }

    &--member-comments {
      width: 220px;
      flex-shrink: 0;
    }

    &--staff-comments {
      width: 220px;
      flex-shrink: 0;
    }
  }

  &__section-title {
    font-size: $fs-s;
    font-weight: bold;
    border-bottom: 2px solid $l-blue;
    padding-bottom: 4px;
    margin-bottom: 8px;
  }

  &__info-table {
    width: 100%;
    border-collapse: collapse;
    font-size: $fs-s;

    th,
    td {
      border: 1px solid $grey11;
      padding: 4px 8px;
      text-align: left;
      vertical-align: top;
      word-break: break-all;
    }

    th {
      background-color: $grey03;
      font-weight: bold;
      white-space: nowrap;
      width: 90px;
    }
  }

  &__pre-wrap {
    white-space: pre-wrap;
    word-break: break-all;
  }

  &__activities-wrap {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(max(90px, calc(33.33% - 6px)), 1fr));
    gap: 8px;
  }

  &__activity-card {
    border: 1px solid $grey11;
    border-radius: 4px;
    padding: 8px;
    font-size: $fs-s;
  }

  &__activity-date {
    font-weight: bold;
    margin-bottom: 4px;
    color: $grey05;
  }

  &__activity-desc {
    white-space: pre-wrap;
    word-break: break-all;
    line-height: 1.4;
  }

  &__member-comments,
  &__manager-comments {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__member-comment,
  &__manager-comment {
    border: 1px solid $grey11;
    border-radius: 4px;
    padding: 6px 8px;
    font-size: $fs-s;
  }

  &__member-comment {
    background-color: $grey15;
  }

  &__manager-comment {
    background-color: $warning-background;
  }

  &__comment-author {
    font-weight: bold;
    display: block;
    margin-bottom: 2px;
  }

  &__comment-date {
    font-weight: normal;
    font-size: $fs-s;
    color: $grey05;
    margin-left: 4px;
  }

  &__comment-detail {
    white-space: pre-wrap;
    word-break: break-all;
    line-height: 1.4;
  }
}

@media (max-width: 767px) {
  .path-surveys-list {
    &__user-row > .d-flex {
      flex-direction: column;
    }

    &__user-info {
      width: 100%;
      border-right: none;
      border-bottom: 1px solid $grey11;
    }
  }
}
</style>
